import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import { useQuery } from '@apollo/client';
import { LIST_ROUTES } from '../graphql/queries';

interface OnboardingContextType {
    hasSeenWelcome: boolean;
    hasCustomTemplate: boolean;
    hasRoutes: boolean;
    routesLoading: boolean;
    showExampleWorkflowsModal: boolean;
    setHasSeenWelcome: (value: boolean) => void;
    setHasCustomTemplate: (value: boolean) => void;
    setShowExampleWorkflowsModal: (value: boolean) => void;
}

const OnboardingContext = createContext<OnboardingContextType | undefined>(undefined);

export const useOnboarding = () => {
    const context = useContext(OnboardingContext);
    if (!context) {
        throw new Error('useOnboarding must be used within an OnboardingProvider');
    }
    return context;
};

interface OnboardingProviderProps {
    children: ReactNode;
}

export const OnboardingProvider: React.FC<OnboardingProviderProps> = ({ children }) => {
    // Query routes to determine if user has any routes
    const { data: routesData, loading: routesLoading, called } = useQuery(LIST_ROUTES);
    const hasRoutes = (routesData?.listRoutes?.length ?? 0) > 0;
    // Consider loading until we have actually fetched data (called and not loading and data exists)
    const isRoutesReady = called && !routesLoading && routesData !== undefined;

    // Get initial state from localStorage
    const [hasSeenWelcome, setHasSeenWelcomeState] = useState<boolean>(() => {
        return localStorage.getItem('onboarding_hasSeenWelcome') === 'true';
    });

    const [hasCustomTemplate, setHasCustomTemplateState] = useState<boolean>(() => {
        return localStorage.getItem('onboarding_hasCustomTemplate') === 'true';
    });

    const [showExampleWorkflowsModal, setShowExampleWorkflowsModal] = useState<boolean>(false);

    // Persist hasSeenWelcome to localStorage
    useEffect(() => {
        localStorage.setItem('onboarding_hasSeenWelcome', String(hasSeenWelcome));
    }, [hasSeenWelcome]);

    // Persist hasCustomTemplate to localStorage
    useEffect(() => {
        localStorage.setItem('onboarding_hasCustomTemplate', String(hasCustomTemplate));
    }, [hasCustomTemplate]);

    const setHasSeenWelcome = (value: boolean) => setHasSeenWelcomeState(value);
    const setHasCustomTemplate = (value: boolean) => setHasCustomTemplateState(value);

    return (
        <OnboardingContext.Provider
            value={{
                hasSeenWelcome,
                hasCustomTemplate,
                hasRoutes,
                routesLoading: !isRoutesReady,
                showExampleWorkflowsModal,
                setHasSeenWelcome,
                setHasCustomTemplate,
                setShowExampleWorkflowsModal
            }}
        >
            {children}
        </OnboardingContext.Provider>
    );
};
